import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const pad = (n) => String(n).padStart(2, '0')
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const parseIso = (s) => new Date(`${s}T00:00:00`)

const ALWAYS_ON_END = '2099-12-31'

export const ScheduleTimeline = ({ anchor, schedules, shifts, shiftFilter, onPeriodClick, onEmptyDayClick }) => {
    const { t } = useTranslation()

    const monthStart = useMemo(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1), [anchor])
    const monthEnd = useMemo(() => new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0), [anchor])
    const daysInMonth = monthEnd.getDate()

    const monthStartIso = toIso(monthStart)
    const monthEndIso = toIso(monthEnd)
    const todayIso = toIso(new Date())

    const rows = useMemo(() => {
        let visibleShifts = shifts
        if (shiftFilter) visibleShifts = shifts.filter((s) => String(s.id) === String(shiftFilter))
        return visibleShifts.map((shift) => {
            const matching = schedules.filter((p) => {
                const ws = p.work_shift || {}
                const sid = p.work_shift_id ?? ws.id
                if (String(sid) !== String(shift.id)) return false
                return p.end_date >= monthStartIso && p.start_date <= monthEndIso
            })
            return { shift, periods: matching }
        })
    }, [shifts, shiftFilter, schedules, monthStartIso, monthEndIso])

    const dayHeaders = []
    for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(anchor.getFullYear(), anchor.getMonth(), i)
        dayHeaders.push({ day: i, weekday: d.toLocaleDateString(undefined, { weekday: 'narrow' }), iso: toIso(d), isWeekend: d.getDay() === 0 || d.getDay() === 6 })
    }

    if (rows.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500 border rounded bg-white">
                {t('workforce.schedule.no_shifts')}
            </div>
        )
    }

    return (
        <div className="bg-white border rounded-lg overflow-auto">
            <div style={{ minWidth: `${200 + daysInMonth * 36}px` }}>
                <div
                    className="grid border-b border-gray-200 bg-gray-50 sticky top-0 z-[1]"
                    style={{ gridTemplateColumns: `200px repeat(${daysInMonth}, minmax(28px, 1fr))` }}
                >
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase border-r border-gray-200">{t('workforce.schedule.shift')}</div>
                    {dayHeaders.map((h) => (
                        <div
                            key={h.iso}
                            className={`text-center text-[11px] py-1 ${h.isWeekend ? 'bg-gray-100 text-gray-500' : 'text-gray-600'} ${h.iso === todayIso ? 'text-yellow-700 font-semibold' : ''}`}
                        >
                            <div className="text-gray-400 text-[10px] uppercase">{h.weekday}</div>
                            <div className="leading-none">{h.day}</div>
                        </div>
                    ))}
                </div>

                {rows.map(({ shift, periods }) => (
                    <div
                        key={shift.id}
                        className="grid border-b border-gray-100 relative"
                        style={{ gridTemplateColumns: `200px repeat(${daysInMonth}, minmax(28px, 1fr))` }}
                    >
                        <div className="px-3 py-3 border-r border-gray-200 flex items-start gap-2 min-w-0">
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900 truncate">{shift.name}</div>
                                <div className="text-[11px] text-gray-500 truncate">
                                    {(shift.start_time || '').slice(0, 5)} → {(shift.end_time || '').slice(0, 5)}
                                </div>
                            </div>
                            {shift.shift_period && (
                                <span className={`mt-0.5 inline-flex px-1 py-0.5 text-[10px] rounded border ${shift.shift_period === 'night' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-yellow-50 text-yellow-800 border-yellow-200'}`}>
                                    {t(`workforce.shifts.period_${shift.shift_period}`)}
                                </span>
                            )}
                        </div>

                        {dayHeaders.map((h) => (
                            <button
                                key={`cell-${shift.id}-${h.iso}`}
                                type="button"
                                onClick={() => onEmptyDayClick(h.iso, shift.id)}
                                title={t('workforce.schedule.schedule_on', { date: h.iso })}
                                className={`relative h-14 border-r border-gray-100 ${h.isWeekend ? 'bg-gray-50/60' : 'bg-white'} ${h.iso === todayIso ? 'bg-yellow-50/40' : ''} hover:bg-yellow-50/40 focus:outline-none focus:bg-yellow-50/60`}
                            />
                        ))}

                        {periods.map((p) => {
                            const startIso = p.start_date < monthStartIso ? monthStartIso : p.start_date
                            const endIso = p.end_date > monthEndIso ? monthEndIso : p.end_date
                            const startCol = parseIso(startIso).getDate()
                            const endCol = parseIso(endIso).getDate()
                            const extendsLeft = p.start_date < monthStartIso
                            const extendsRight = p.end_date > monthEndIso
                            const isAlwaysOn = p.end_date === ALWAYS_ON_END
                            const period = (p.work_shift || shift).shift_period || shift.shift_period
                            const color = period === 'night'
                                ? 'bg-indigo-100 border-indigo-300 text-indigo-900 hover:bg-indigo-200'
                                : 'bg-yellow-100 border-yellow-300 text-yellow-900 hover:bg-yellow-200'
                            return (
                                <button
                                    key={`period-${p.id}`}
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onPeriodClick({ ...p, work_shift: p.work_shift || shift }) }}
                                    title={`${p.start_date} → ${p.end_date === ALWAYS_ON_END ? '∞' : p.end_date}`}
                                    className={`absolute top-2 bottom-2 my-auto h-8 rounded-md border text-[11px] font-medium px-2 flex items-center gap-1 truncate transition-colors ${color} ${isAlwaysOn ? 'border-dashed opacity-80' : ''}`}
                                    style={{
                                        left: `calc(200px + (100% - 200px) * ${(startCol - 1) / daysInMonth})`,
                                        width: `calc((100% - 200px) * ${(endCol - startCol + 1) / daysInMonth})`,
                                    }}
                                >
                                    {extendsLeft && <span className="text-[10px]">←</span>}
                                    <span className="truncate">
                                        {isAlwaysOn ? t('workforce.schedule.always_on_tag') : `${p.start_date.slice(5)} → ${p.end_date.slice(5)}`}
                                    </span>
                                    {extendsRight && <span className="ml-auto text-[10px]">→</span>}
                                </button>
                            )
                        })}
                    </div>
                ))}
            </div>
        </div>
    )
}
